import { useState, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Camera, Upload, Sparkles, X, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import DisclaimerBanner from "../components/DisclaimerBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Diagnose() {
  const { t, i18n } = useTranslation();
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/.test(file.type)) {
      toast.error(t("diagnosis.invalidImageError"));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      setImageBase64(e.target.result.split(",")[1]);
      setResult(null);
    };
    reader.readAsDataURL(file);
  };

  const analyze = async () => {
    if (!imageBase64) return;
    setAnalyzing(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/diagnose`,
        { image_base64: imageBase64, language: i18n.language }, { withCredentials: true, timeout: 60000 });
      setResult(r.data);
    } catch (e) {
      toast.error(t("diagnosis.analysisError"));
    } finally { setAnalyzing(false); }
  };

  const reset = () => { setImagePreview(null); setImageBase64(null); setResult(null); };

  return (
    <div className="min-h-screen pb-24" data-testid="diagnose-page">
      <Header title={t("drawer.items.diagnose")} />
      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        <div className="vs-card p-5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={20} style={{ color: "var(--accent)" }} />
            <h2 className="heading text-xl font-bold">{t("diagnosis.heading")}</h2>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
            {t("diagnosis.descriptionPrefix")} <strong>{t("diagnosis.descriptionBold")}</strong>
            {t("diagnosis.descriptionSuffix")}
          </p>

          {!imagePreview ? (
            <div className="space-y-3">
              <button
                onClick={() => cameraRef.current?.click()}
                className="vs-btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="camera-btn"
              >
                <Camera size={18} /> {t("diagnosis.cameraButton")}
              </button>
              <button
                onClick={() => fileRef.current?.click()}
                className="vs-btn-secondary w-full inline-flex items-center justify-center gap-2"
                data-testid="upload-btn"
              >
                <Upload size={18} /> {t("diagnosis.uploadButton")}
              </button>
              <input ref={cameraRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment"
                onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" data-testid="camera-input" />
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" data-testid="file-input" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="relative rounded-xl overflow-hidden" style={{ border: "2px solid var(--border)" }}>
                <img src={imagePreview} alt={t("diagnosis.previewAlt")} className="w-full h-64 object-cover" data-testid="preview-img" />
                <button onClick={reset} className="absolute top-2 right-2 p-2 rounded-full"
                  style={{ background: "rgba(17,20,17,0.8)", color: "white" }} data-testid="reset-btn">
                  <X size={18} />
                </button>
              </div>
              {!result && (
                <button onClick={analyze} disabled={analyzing}
                  className="vs-btn-accent w-full inline-flex items-center justify-center gap-2"
                  data-testid="analyze-btn">
                  {analyzing ? <><div className="vs-spinner" style={{ borderTopColor: "white", borderColor: "rgba(255,255,255,0.3)" }} /> {t("diagnosis.analyzingLabel")}</> : <><Sparkles size={18} /> {t("diagnosis.analyzeButton")}</>}
                </button>
              )}
            </div>
          )}
        </div>

        {result && (
          <div className="vs-card p-5 vs-fade-up" data-testid="diagnose-result">
            {!result.is_grape ? (
              <div className="text-center py-4">
                <AlertTriangle size={36} style={{ color: "var(--warning)" }} className="mx-auto mb-2" />
                <p className="font-semibold">{t("diagnosis.notGrapeTitle")}</p>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                  {t("diagnosis.notGrapeHint")}
                </p>
              </div>
            ) : (result.diseases || []).length === 0 ? (
              <div className="text-center py-4">
                <CheckCircle2 size={36} style={{ color: "var(--primary)" }} className="mx-auto mb-2" />
                <p className="font-semibold">{t("diagnosis.noDiseaseTitle")}</p>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                  {t("diagnosis.noDiseaseHint")}
                </p>
                {result.general_notes && (
                  <p className="text-xs mt-3 italic" style={{ color: "var(--text-muted)" }}>{result.general_notes}</p>
                )}
              </div>
            ) : (
              <>
                <h3 className="heading text-xl font-bold mb-3">{t("diagnosis.resultsTitle")}</h3>
                {(result.diseases || []).map((d, i) => (
                  <div key={i} className="p-4 rounded-lg mb-3"
                    style={{ background: "var(--secondary)", border: "2px solid var(--border)" }}
                    data-testid={`disease-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="heading font-bold text-lg">{d.name}</h4>
                      <span className="vs-pill" style={{
                        background: d.confidence === "alta" ? "var(--primary)" : "var(--warning)",
                        color: "white", borderColor: "transparent",
                      }}>
                        {t("diagnosis.confidenceLabel")} {d.confidence}
                      </span>
                    </div>
                    {d.severity && (
                      <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>
                        {t("diagnosis.severityLabel")} <strong>{d.severity}</strong>
                      </div>
                    )}
                    {d.symptoms_observed && (
                      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                        <strong>{t("diagnosis.symptomsLabel")}</strong> {d.symptoms_observed}
                      </p>
                    )}
                  </div>
                ))}
                {result.treatment && (
                  <div className="p-4 rounded-lg mt-2"
                    style={{ background: "var(--surface)", border: "2px solid var(--primary)" }}>
                    <h4 className="heading font-bold text-base mb-2" style={{ color: "var(--primary)" }}>
                      {t("diagnosis.treatmentTitle")}
                    </h4>
                    {result.treatment.immediate_actions?.length > 0 && (
                      <div className="mb-2">
                        <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>{t("diagnosis.immediateActionsLabel")}</div>
                        <ul className="text-sm list-disc pl-5">
                          {result.treatment.immediate_actions.map((a, i) => <li key={i}>{a}</li>)}
                        </ul>
                      </div>
                    )}
                    {result.treatment.recommended_products?.length > 0 && (
                      <div className="mb-2">
                        <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>{t("diagnosis.recommendedProductsLabel")}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {result.treatment.recommended_products.map((p, i) => <span key={i} className="vs-pill">{p}</span>)}
                        </div>
                      </div>
                    )}
                    {result.treatment.prevention && (
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>{t("diagnosis.preventionLabel")}</div>
                        <p className="text-sm">{result.treatment.prevention}</p>
                      </div>
                    )}
                  </div>
                )}
                {result.general_notes && (
                  <p className="text-xs mt-3 italic" style={{ color: "var(--text-muted)" }}>{result.general_notes}</p>
                )}
              </>
            )}
            <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <DisclaimerBanner variant="short" />
            </div>
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
